import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ENTITY_QUERY_KEYS } from 'app/modules/entities/queries/keys';
import { UNIT21_ID_FIELD_NOT_FOUND } from 'app/modules/search/constants';
import { EntitySearchPayload } from 'app/modules/search/payload';
import {
  EntitySearchV2Response,
  EntitySource,
} from 'app/modules/search/response';
import { selectHasReadEntitiesPermissions } from 'app/modules/session/selectors';
import { post } from 'app/shared/utils/fetchr';
import { unionBy } from 'lodash';
import { useMemo, useReducer } from 'react';
import { useSelector } from 'react-redux';

const getEntitySources = (data: EntitySearchV2Response): EntitySource[] =>
  data.hits.map((hit) => hit.source);

export const searchEntity = (payload?: EntitySearchPayload) =>
  post<EntitySearchV2Response>('/v1/entities/search/v2', payload);

export const useEntitySearch = (
  payload?: EntitySearchPayload,
  enabled?: boolean,
) => {
  const queryClient = useQueryClient();
  const hasReadEntitiesPermissions = useSelector(
    selectHasReadEntitiesPermissions,
  );

  const query = useQuery<EntitySearchV2Response, Error, EntitySource[]>({
    enabled: Boolean(enabled && !!payload && hasReadEntitiesPermissions),
    // can't garbage collect since we return all transactions in the query history
    gcTime: Infinity,
    queryFn: () => searchEntity(payload),
    queryKey: ENTITY_QUERY_KEYS.getEntitySearch(payload),
    select: (data) => getEntitySources(data),
    staleTime: 5 * 60 * 1000,
  });

  // call to manually refresh entities from cached data
  const [, refreshEntities] = useReducer((x) => x + 1, 0);

  const cachedQueryData = queryClient.getQueriesData<EntitySearchV2Response>({
    queryKey: ENTITY_QUERY_KEYS.getEntitySearch(),
  });

  const entities = useMemo(() => {
    const allEntities = cachedQueryData.reduce<EntitySource[]>((acc, i) => {
      const [, queryData] = i;
      if (queryData) {
        return unionBy(getEntitySources(queryData), acc, 'external_id');
      }
      return acc;
    }, []);

    return allEntities.filter((e) => e.unit21_id !== UNIT21_ID_FIELD_NOT_FOUND);
  }, [cachedQueryData]);

  return {
    query,
    entities,
    refreshEntities,
  };
};
